/**
 * useEditorStore — zentraler State für den FieldEditor.
 *
 * Lebenszyklus:
 *   load({projectId, site, lang, page})  // fetch Schema + Werte
 *   setField(key, value)                  // optimistic (NUR lokal, kein Save)
 *   saveNow()                             // explizit (Cmd+S, Save-Button) — mit Toast + Rebuild
 *   reset()                               // beim Verlassen der Edit-View
 *
 * Save-Pattern (Sanity Studio / Word-Style):
 *   - KEIN Auto-Save. setField() updated nur lokal (optimistic), Server bekommt
 *     nichts bis saveNow() explizit gerufen wird.
 *   - Cmd+S + Save-Button + "Speichern"-Aktion sind die einzigen Save-Trigger.
 *   - Safety-Net: ProjectEditView nutzt beforeunload + Vue-Router-Guard um den
 *     User zu warnen wenn er die Page mit dirty Edits verlassen will.
 *   - 422 → fieldErrors befüllen, baseline NICHT updaten
 *   - 200 → baseline := values, Toast + Preview-Rebuild
 *
 * Auto-Save wurde am 2026-05-23 entfernt (David-Feedback): 3s Debounce
 * triggerte während normaler Tipp-Pausen → unterbrach Workflow. Bewusste
 * Save-Aktion ist klarer und matched das Word/Sanity-Studio-Pattern.
 */
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { api, ApiError } from '@/lib/api'
import type { JsonSchema, JsonSchemaProperty, SaveResponse,
  PreviewStatusResponse, AltPruefEintrag,
} from '@/types/api'
import { useToast } from '@/composables/useToast'
import { useKundenRechte } from '@/composables/useKundenRechte'
import { uebersetze } from '@/lib/uebersetzen'
import { fehlersatz } from '@/lib/fehlersatz'
import { pruefsatz } from '@/lib/pruefsatz'
import { useSeitenplanStore, zerlege } from '@/stores/seitenplan'

/** Ein Feld, wie es der Kunde es gerade sieht, bevor gebaut wurde. */
export interface FeldEntwurf {
  art: 'bild' | 'text' | 'html'
  wert: string
}

export interface EditorContext {
  projectId: string
  site: string
  lang: string
  page: string
}

/** Ein Feld aus der Vorschau: Schlüssel + die Datei (Seite/Gruppe), in der er liegt. */
export interface FieldRef {
  key: string
  page: string
}

/**
 * Eindeutige Adresse eines Feldes über Seiten-Grenzen hinweg.
 * Nötig, seit die Feldliste Texte mehrerer Gruppen zeigt (Welle 4c): derselbe
 * Schlüssel kann in zwei Dateien vorkommen (z.B. `title` in index und in about).
 */
export function fieldRef(page: string, key: string): string {
  return page + '::' + key
}

export const useEditorStore = defineStore('editor', () => {
  const ctx = ref<EditorContext | null>(null)
  const schema = ref<JsonSchema | null>(null)
  /**
   * Werte je Seite/Gruppe. Der Fields-Endpunkt liefert ohnehin alle Gruppen einer
   * Sprache in einem Aufruf — wir behalten sie, damit auch geteilte Inhalte
   * (Navigation, Footer, Preise) direkt auf der Seite editierbar sind, auf der
   * sie erscheinen. Gespeichert wird weiterhin pro Datei.
   */
  const pageValues = ref<Record<string, Record<string, string>>>({})
  /**
   * Beschriftung einzelner Felder, vom Server mitgeliefert (Seite → Feld → Name).
   * Nötig für Felder, die erst zur Laufzeit entstehen: die Blöcke einer
   * Doku-Seite heißen b1, b2, … und wären sonst unlesbar.
   */
  const fieldLabels = ref<Record<string, Record<string, string>>>({})
  const pageBaseline = ref<Record<string, Record<string, string>>>({})
  // Werte der ANDEREN Sprachen (lang → gruppe → {key: value}), read-only.
  // Dient als Übersetzungs-Referenz im Feld-Editor (z.B. Englisch unter dem
  // Thai-Feld, damit Nahm den Originaltext beim Korrigieren sieht).
  //
  // Die Gruppe steht bewusst mit drin: seit die Feldliste Texte mehrerer
  // Gruppen zeigt, kommt derselbe Schlüssel (`title`) in mehreren Dateien vor —
  // und bei den Showcase-Demos liegt fast jedes Feld in einer anderen Gruppe
  // als die geöffnete Seite. Ohne Gruppen-Ebene wäre die Referenz dort leer.
  const referenceValues = ref<Record<string, Record<string, Record<string, string>>>>({})
  const focusedField = ref<string | null>(null)
  /**
   * Seite/Gruppe des fokussierten Feldes. null = aktive Seite (Normalfall).
   * Gesetzt, wenn ein Feld aus einem geteilten Bereich editiert wird.
   */
  const focusedPage = ref<string | null>(null)
  /**
   * Textstelle, auf die in der Vorschau geklickt wurde. Bei langen Feldern
   * (der ganze Text einer Doku-Seite ist EIN Feld) setzt der Editor den Cursor
   * damit auf den angeklickten Absatz, statt ans Ende zu springen.
   */
  const focusHint = ref<string | null>(null)
  /**
   * Was sonst noch an der angeklickten Stelle hängt.
   *
   * Ein Element trägt oft mehr als ein Feld: ein Bild sich selbst und seinen
   * Alt-Text, ein Link Text und Ziel, ein Eingabefeld seinen Platzhalter. Ein
   * Klick kann nur eines öffnen — die übrigen waren dadurch nur über die
   * Feldliste erreichbar, bei einem Bild ausgerechnet der Alt-Text.
   *
   * Der Editor bietet sie deshalb als Zeile über dem Feld an.
   *
   * Die Liste enthält ALLE Felder der Stelle, auch das gerade geöffnete — der
   * Editor blendet nur das aktuelle aus. Ohne diesen Umweg gäbe es keinen
   * Rückweg: Wer vom Bild zum Alt-Text wechselt, stünde dort vor einer leeren
   * Liste und müsste erneut in die Vorschau klicken.
   */
  const focusFelder = ref<{ id: string; art: string }[]>([])
  /** Zählt jeden Fokus-Vorgang, auch den auf dasselbe Feld. */
  const focusZaehler = ref(0)
  /**
   * Was die Vorschau gemeldet hat: alle Felder, die auf der gezeigten Seite
   * wirklich vorkommen — in Dokument-Reihenfolge. null = noch keine Meldung
   * (z.B. Markdown-Projekte ohne Vorschau) → die Feldliste fällt auf die
   * Felder der gewählten Seite zurück.
   */
  const inventory = ref<FieldRef[] | null>(null)
  const loading = ref(false)
  const saving = ref(false)
  const rebuilding = ref(false)
  /** Läuft gerade eine Struktur-Änderung? Sperrt die Knöpfe. */
  const strukturLaeuft = ref(false)
  /** Seiten mit Seitenplan (Baukasten), aus der letzten Feldliste. */
  let seitenplaene: string[] = []
  const lastSavedAt = ref<number | null>(null)
  const lastRebuildAt = ref<number | null>(null)
  const fieldErrors = ref<Map<string, string>>(new Map())
  /**
   * Bildbeschreibungen, die nach einem Bildtausch zu prüfen sind, je
   * fieldRef(gruppe, beschreibung). Kommen vom Server (nach dem Speichern
   * gemerkt, Domain/Content/AltPruefung) und aus diesem Fenster (gleich nach
   * dem Hochladen, bevor gespeichert ist). Ob ein Eintrag gerade gilt, sagt
   * altPruefung().
   */
  const altPruefungen = ref<Map<string, AltPruefEintrag>>(new Map())

  let pendingResave = false
  const toast = useToast()

  /** Werte der aktiven Seite (die weitaus meisten Felder). */
  const values = computed<Record<string, string>>(
    () => (ctx.value ? pageValues.value[ctx.value.page] ?? {} : {}),
  )
  const baseline = computed<Record<string, string>>(
    () => (ctx.value ? pageBaseline.value[ctx.value.page] ?? {} : {}),
  )

  /** Geänderte Felder der aktiven Seite (nur Schlüssel). */
  const dirty = computed(() => {
    const result = new Set<string>()
    const keys = new Set([
      ...Object.keys(values.value),
      ...Object.keys(baseline.value),
    ])
    for (const k of keys) {
      if ((values.value[k] ?? '') !== (baseline.value[k] ?? '')) result.add(k)
    }
    return result
  })

  /** Geänderte Felder über ALLE Seiten hinweg, als `<seite>::<key>`. */
  const dirtyRefs = computed(() => {
    const result = new Set<string>()
    for (const pg of Object.keys(pageValues.value)) {
      const vals = pageValues.value[pg] ?? {}
      const base = pageBaseline.value[pg] ?? {}
      for (const k of new Set([...Object.keys(vals), ...Object.keys(base)])) {
        if ((vals[k] ?? '') !== (base[k] ?? '')) result.add(fieldRef(pg, k))
      }
    }
    return result
  })
  const isDirty = computed(() => dirtyRefs.value.size > 0)

  /** Seiten (Textgruppen) mit ungespeicherten Änderungen, für die Seitenliste. */
  const ungespeicherteSeiten = computed<string[]>(() => {
    const seiten = new Set<string>()
    for (const ref_ of dirtyRefs.value) seiten.add(ref_.slice(0, ref_.indexOf('::')))
    return [...seiten]
  })

  /**
   * Was der Kunde gerade geändert hat, aber noch nicht gebaut ist.
   *
   * Die Vorschau zeigt den letzten Bau. Ein hochgeladenes Bild liegt schon im
   * Repo, ein getippter Text noch gar nirgends — ohne diese Liste stand nach
   * dem Hochladen weiter „Bild wählen" in der Vorschau und beim Tippen
   * „(MISSING: body)" (David, 18./19.08.2026).
   *
   * Immer VOLLSTÄNDIG, nie als Einzelmeldung: Setzt der Kunde ein Feld wieder
   * zurück, verschwindet es hier, und die Vorschau stellt es zurück.
   *
   * Bilder zeigen auf die Datei im REPO (media-Endpunkt), nicht in den Bau —
   * dort liegen sie erst nach dem nächsten Durchlauf.
   */
  const offeneEntwuerfe = computed<Record<string, FeldEntwurf>>(() => {
    const out: Record<string, FeldEntwurf> = {}
    const id = ctx.value?.projectId
    if (id === undefined) return out
    for (const key of dirty.value) {
      const ui = propFor(key, null)?.['x-cms-ui']
      const wert = values.value[key] ?? ''
      if (ui?.widget === 'image') {
        const bild = wert.trim()
        out[key] = {
          art: 'bild',
          wert: bild === '' || /^https?:\/\//.test(bild) ? bild : api.mediaUrl(id, bild),
        }
        continue
      }
      out[key] = { art: ui?.widget === 'richtext' ? 'html' : 'text', wert }
    }
    return out
  })

  /** Auflösung „welche Datei" — fehlt die Angabe, ist es die aktive Seite. */
  function pageOf(page?: string | null): string {
    return page ?? ctx.value?.page ?? ''
  }

  function valueOf(key: string, page?: string | null): string {
    return pageValues.value[pageOf(page)]?.[key] ?? ''
  }

  function isFieldDirty(key: string, page?: string | null): boolean {
    return dirtyRefs.value.has(fieldRef(pageOf(page), key))
  }

  function fieldError(key: string, page?: string | null): string | undefined {
    return fieldErrors.value.get(fieldRef(pageOf(page), key))
  }

  /** Vom Server mitgelieferte Beschriftung eines Feldes, falls es eine gibt. */
  function fieldLabel(key: string, page?: string | null): string | undefined {
    return fieldLabels.value[pageOf(page)]?.[key]
  }

  /**
   * Die Schema-Regel eines Feldes AUF DIESER SEITE.
   *
   * Das Schema ist flach: Es kennt Feldnamen, keine Seiten. Meistens stimmt
   * das. Bei UnderWave nicht — dort heisst der Artikeltext `body` und die
   * Datenschutzerklaerung auch, aber die enthaelt Tabellen, die der
   * Schreib-Editor nicht kann. Eine Regel kann sich deshalb mit `seiten` auf
   * bestimmte Seiten beschraenken; ein Stern am Ende steht fuer eine Vorsilbe
   * (`article-*`). Ohne Angabe gilt sie ueberall, also wie bisher.
   *
   * Serverseitig entscheidet dasselbe: Domain/Schema/SchemaSicht.php. Beide
   * muessen zusammenpassen, sonst zeigt das Panel einen Editor an, dessen
   * Ergebnis der Server wieder wegputzt.
   */
  function propFor(key: string, page?: string | null): JsonSchemaProperty | undefined {
    // Abschnitte einer Baukasten-Seite (`k7f2:titel`) stehen nicht im Schema
    // der Site, ihre Regel steht im Baustein. Siehe stores/seitenplan.ts.
    const ausPlan = useSeitenplanStore().propFuer(key, pageOf(page))
    if (ausPlan) return ausPlan
    const prop = schema.value?.properties[key]
    if (!prop) return undefined
    const seiten = prop['x-cms-ui']?.seiten
    if (!seiten || seiten.length === 0) return prop
    const p = pageOf(page)
    const passt = seiten.some((m) => (m.endsWith('*') ? p.startsWith(m.slice(0, -1)) : m === p))
    return passt ? prop : undefined
  }

  /** Kennt die Feldliste diese Gruppe? (Werte sind geladen, also editierbar.) */
  function hasPage(page: string): boolean {
    return pageValues.value[page] !== undefined
  }

  const allKeys = computed<string[]>(() => {
    // Bug-Fix #15: Schema-Keys NICHT mehr global einbeziehen — das mischt alle
    // Pages und überflutet die Field-Liste mit Tausenden Feldern.
    // Stattdessen: NUR die keys die tatsächlich in der aktuellen Page (values)
    // sind. Schema bleibt für Validation + UI-hints (group, label) verfügbar.
    //
    // Phase Y.2: KEIN .sort() — Object.keys behält JSON-Source-Order (= visuell
    // top→bottom wie im Template). Das ist Industry-Standard (Statamic Blueprints,
    // Sanity Schema, Contentful Content-Type). Alphabetisch wäre für Editoren
    // verwirrend (Hero/CTA/Footer durcheinander).
    return Object.keys(values.value)
  })

  /** Ein Neubau der Vorschau wurde angefordert, während einer lief. */
  let rebuildNachholen = false
  /** Wie oft der Neubau schon an einer fremden Sperre abgeprallt ist. */
  let uebersprungen = 0
  const MAX_UEBERSPRUNGEN = 3

  /** Zeigt der Editor noch auf denselben Ort wie beim Start der Anfrage? */
  function istNochAktuell(c: EditorContext): boolean {
    return ctx.value?.projectId === c.projectId
      && ctx.value?.site === c.site
      && ctx.value?.lang === c.lang
  }

  /**
   * Server-Werte, darüber was hier getippt und noch nicht gespeichert ist.
   *
   * Seit dem 27.10.2026 kann Ungespeichertes auf mehreren Seiten zugleich
   * stehen (Seitenwechsel ohne Speichern, `wechsleSeite`). Kein Neuladen darf
   * es still verwerfen, auch nicht nach „Verlauf: zurückholen“ oder einer
   * zurückgenommenen Claude-Änderung. Nur Texte eines Abschnitts oder
   * Eintrags, den es beim Server nicht mehr gibt, fallen weg: Er nähme sie
   * auch nicht mehr an.
   */
  function mitUngespeichertem(server: Record<string, Record<string, string>>): Record<string, Record<string, string>> {
    const raus: Record<string, Record<string, string>> = {}
    for (const [pg, werte] of Object.entries(server)) {
      const lokal = pageValues.value[pg] ?? {}
      const alt = pageBaseline.value[pg] ?? {}
      const neu = { ...werte }
      for (const k of Object.keys(lokal)) {
        if ((lokal[k] ?? '') === (alt[k] ?? '')) continue
        if (zerlege(k) !== null && werte[k] === undefined) continue
        neu[k] = lokal[k] ?? ''
      }
      raus[pg] = neu
    }
    return raus
  }

  async function load(c: EditorContext): Promise<void> {
    // Dieselben Texte wie bisher (Projekt, Bereich, Sprache): Ungespeichertes
    // bleibt stehen. Sonst gehört es zu anderen Texten und gilt hier nicht.
    const gleicheTexte = ctx.value !== null && ctx.value.projectId === c.projectId
      && ctx.value.site === c.site && ctx.value.lang === c.lang
    ctx.value = c
    loading.value = true
    fieldErrors.value = new Map()
    // Das offene Feld gehört zum alten Projekt, Bereich oder zur alten Sprache.
    // Blieb es stehen, zeigte das rechte Panel nach dem Wechsel weiter denselben
    // Feldnamen — mit dem Wert von woanders (Audit 2026-08-05).
    focusedField.value = null
    focusedPage.value = null
    altPruefungen.value = new Map()
    try {
      // WICHTIG: Editier-Werte IMMER über den bewährten Weg laden (lang gesetzt).
      // Die Referenz-Sprachen kommen separat + abgesichert (siehe unten) — sie
      // dürfen das Editieren NIE blockieren.
      const resp = await api.projectFields(c.projectId, {
        site: c.site,
        lang: c.lang,
      })
      // Nach dem Warten prüfen, ob der Kontext noch derselbe ist. Wer schnell
      // zweimal die Sprache umschaltet, bekam sonst die zuletzt EINTREFFENDE
      // Antwort in den Speicher gelegt — also womöglich die Werte der falschen
      // Sprache, mit dem Kontext der neuen (Audit 2026-08-05). Die
      // Referenzsprachen prüfen das längst, das Hauptladen nicht.
      if (!istNochAktuell(c)) return
      const siteData = resp.sites[c.site]
      const langData = siteData?.langs[c.lang]
      // Eine Seite MUSS keine eigene Textdatei haben: bei den Showcase-Demos
      // besteht die Startseite aus 20 Abschnitten, "index" gibt es als Gruppe
      // gar nicht. Deshalb zählt nur, ob es für Bereich + Sprache überhaupt
      // Texte gibt — vorher meldete der Editor dort „Page not found" und warf
      // dabei ALLE geladenen Werte weg, die Feldliste blieb leer (2026-08-04).
      const gruppen = langData?.pages ?? {}
      if (Object.keys(gruppen).length === 0) {
        toast.error(uebersetze('meldung.keine_texte'), `${c.site}/${c.lang}`)
        pageValues.value = {}
        pageBaseline.value = {}
        referenceValues.value = {}
        schema.value = null
        return
      }
      schema.value = siteData?.schema ?? null
      // Baukasten-Seite? Dann Plan und Katalog mitladen, damit Feldliste und
      // Abschnitts-Leiste vom selben Stand sind wie die Texte.
      seitenplaene = siteData?.seitenplaene ?? []
      await useSeitenplanStore().laden(c, seitenplaene)
      if (!istNochAktuell(c)) return
      // Alle Gruppen dieser Sprache behalten (kommen im selben Response) — die
      // Feldliste zeigt auch Texte, die aus Navigation/Footer/Preise stammen.
      const vals: Record<string, Record<string, string>> = {}
      const base: Record<string, Record<string, string>> = {}
      const labels: Record<string, Record<string, string>> = {}
      for (const [pg, pd] of Object.entries(langData?.pages ?? {})) {
        if (!pd || typeof pd.values !== 'object') continue
        vals[pg] = { ...pd.values }
        base[pg] = { ...pd.values }
        if (pd.field_labels) labels[pg] = { ...pd.field_labels }
      }
      pageValues.value = gleicheTexte ? mitUngespeichertem(base) : vals
      pageBaseline.value = base
      fieldLabels.value = labels
      // Kaputte Gruppen melden statt verschweigen: Sie fehlen in der Liste,
      // und ohne Hinweis suchte die Kundin einen Text, den es zu geben scheint.
      const unlesbar = langData?.unlesbar ?? {}
      const namen = Object.keys(unlesbar)
      if (namen.length > 0) {
        toast.error(
          namen.length === 1
            ? uebersetze('meldung.bereich_kaputt', { name: namen[0] })
            : uebersetze('meldung.bereiche_kaputt', {}, namen.length),
          uebersetze('meldung.bereich_kaputt_text'),
        )
      }
      void loadReferences(c) // fire-and-forget, eigener try/catch
      void ladeAltPruefungen(c)
    } catch (e) {
      // 401 heißt: keine Sitzung mehr. „HTTP 401 on /api/v1/…" sagt der
      // Kundin nichts, was sie tun soll; der Satz darunter schon.
      if (e instanceof ApiError && e.status === 401) {
        toast.error(uebersetze('meldung.sitzung_abgelaufen'))
        return
      }
      toast.error(uebersetze('errors.load_failed'), fehlersatz(e))
    } finally {
      loading.value = false
    }
  }

  /**
   * Auf eine andere Seite desselben Projekts, Bereichs und derselben Sprache.
   *
   * Die Texte aller Seiten sind schon geladen (ein Abruf liefert jede Gruppe
   * der Sprache). Was auf der alten Seite getippt und nicht gespeichert ist,
   * bleibt deshalb einfach stehen; gespeichert wird weiter mit einem Klick,
   * für alle Seiten zusammen (`doSave` geht jede geänderte Seite durch).
   * Bis zum 27.10.2026 lud ein Seitenwechsel alles neu, und das Panel musste
   * vorher fragen, ob das Getippte verloren gehen darf. David: „ich kann die
   * seite nicht wechseln ohne auf speichern zu klicken. das muss gehen“.
   *
   * Anderes Projekt, anderer Bereich, andere Sprache: dort gelten andere
   * Texte, also wie bisher `load`.
   */
  async function wechsleSeite(c: EditorContext): Promise<void> {
    const alt = ctx.value
    if (!alt || alt.projectId !== c.projectId || alt.site !== c.site || alt.lang !== c.lang) {
      await load(c)
      return
    }
    ctx.value = c
    focusField(null)
    // Was andere inzwischen gespeichert haben, kommt dazu; Getipptes bleibt.
    // Davor, weil die Antwort auch sagt, welche Seiten einen Plan haben: Eine
    // eben angelegte Baukasten-Seite kennt die alte Liste noch nicht.
    await aktualisiereWerte(c)
    // Schnell weitergeklickt: dann lädt der nächste Wechsel.
    if (!istNochAktuell(c) || ctx.value?.page !== c.page) return
    await useSeitenplanStore().laden(c, seitenplaene)
    void ladeAltPruefungen(c)
  }

  /**
   * Jemand anderes hat geändert (der Assistent, eine zurückgenommene
   * Claude-Änderung): Texte und Aufbau der offenen Seite still nachladen,
   * ohne das offene Feld oder Getipptes anzufassen.
   */
  async function nachFremderAenderung(): Promise<void> {
    const c = ctx.value
    if (!c) return
    await aktualisiereWerte(c)
    if (!istNochAktuell(c) || ctx.value?.page !== c.page) return
    await useSeitenplanStore().laden(c, seitenplaene)
  }

  /**
   * Werte still nachladen, nach einem Umbau an einer Baukasten-Seite.
   *
   * Anders als `load` bleibt alles stehen, was gerade offen ist: das gewählte
   * Feld, die Prüfhinweise und vor allem, was getippt und noch nicht
   * gespeichert ist. Nur Texte eines Abschnitts oder Eintrags, den es nicht
   * mehr gibt, fallen weg; der Server nähme sie auch nicht mehr an.
   */
  async function aktualisiereWerte(c: EditorContext): Promise<void> {
    let resp
    try {
      resp = await api.projectFields(c.projectId, { site: c.site, lang: c.lang })
    } catch {
      return // Der nächste Klick oder Seitenwechsel lädt ohnehin.
    }
    if (!istNochAktuell(c)) return
    seitenplaene = resp.sites[c.site]?.seitenplaene ?? seitenplaene
    const gruppen = resp.sites[c.site]?.langs[c.lang]?.pages ?? {}
    const base: Record<string, Record<string, string>> = {}
    const labels: Record<string, Record<string, string>> = {}
    for (const [pg, pd] of Object.entries(gruppen)) {
      if (!pd || typeof pd.values !== 'object') continue
      base[pg] = { ...pd.values }
      if (pd.field_labels) labels[pg] = { ...pd.field_labels }
    }
    const vals = mitUngespeichertem(base)
    pageValues.value = vals
    pageBaseline.value = base
    fieldLabels.value = labels
    // Stand das offene Feld in einem gelöschten Abschnitt, gibt es nichts mehr zu zeigen.
    const offen = focusedField.value
    if (offen !== null && vals[pageOf(focusedPage.value)]?.[offen] === undefined) focusField(null)
    void loadReferences(c)
  }

  /**
   * Lädt die Werte der ANDEREN Sprachen derselben Seite als Übersetzungs-Referenz.
   * Vollständig abgesichert: jeder Fehler wird geschluckt — die Referenz ist ein
   * Komfort-Feature und darf das Editieren niemals stören.
   */
  async function loadReferences(c: EditorContext): Promise<void> {
    referenceValues.value = {}
    try {
      // Ohne lang → Backend liefert alle Sprachen (ProjectsController.fields).
      const resp = await api.projectFields(c.projectId, { site: c.site })
      const langs = resp?.sites?.[c.site]?.langs
      if (!langs) return
      const refs: Record<string, Record<string, Record<string, string>>> = {}
      for (const [lg, ld] of Object.entries(langs)) {
        if (lg === c.lang) continue
        const gruppen: Record<string, Record<string, string>> = {}
        for (const [pg, daten] of Object.entries(ld?.pages ?? {})) {
          const pv = daten?.values
          if (pv && typeof pv === 'object') gruppen[pg] = { ...pv }
        }
        refs[lg] = gruppen
      }
      // Nur übernehmen, wenn der Kontext noch derselbe ist (kein Race). Die
      // Seite spielt keine Rolle mehr — die Referenz gilt für alle Gruppen.
      if (ctx.value?.projectId === c.projectId
          && ctx.value?.lang === c.lang && ctx.value?.site === c.site) {
        referenceValues.value = refs
      }
    } catch {
      referenceValues.value = {} // still ignorieren
    }
  }

  /**
   * Was der Server zu prüfen hat. Wie die Referenz-Sprachen: Bleibt die
   * Antwort aus, fehlt nur der Hinweis, das Bearbeiten geht weiter.
   */
  async function ladeAltPruefungen(c: EditorContext): Promise<void> {
    try {
      const { eintraege } = await api.altPruefen(c.projectId, c.site, c.lang)
      if (!istNochAktuell(c)) return
      // Was in diesem Fenster gerade hochgeladen wurde, gewinnt: Der Server
      // kennt es erst nach dem Speichern.
      const next = new Map<string, AltPruefEintrag>()
      for (const e of eintraege) next.set(fieldRef(e.gruppe, e.alt), e)
      for (const [ref_, e] of altPruefungen.value) next.set(ref_, e)
      altPruefungen.value = next
    } catch {
      /* Komfort, kein Grund, das Bearbeiten zu stören. */
    }
  }

  /**
   * Die offene Prüfung dieser Beschreibung, oder undefined.
   *
   * Sie gilt nur, solange im Bildfeld genau das Bild steht, für das sie
   * angelegt wurde (wer den Tausch zurücknimmt, braucht keinen Hinweis), und
   * solange die Beschreibung unverändert ist. Wer sie ändert, hat sie
   * geprüft; mit dem Speichern ist sie erledigt.
   */
  function altPruefung(altKey: string, page?: string | null): AltPruefEintrag | undefined {
    const pg = pageOf(page)
    const e = altPruefungen.value.get(fieldRef(pg, altKey))
    if (!e) return undefined
    if (valueOf(e.bildFeld, pg) !== e.bild) return undefined
    if (isFieldDirty(altKey, pg)) return undefined
    return e
  }

  /**
   * Nach dem Hochladen: die Beschreibung dieses Bildfelds vormerken.
   * Gibt ihren Schlüssel zurück, null wenn das Schema keine nennt.
   */
  function bildGetauscht(bildKey: string, page: string | null | undefined, bild: string): string | null {
    const pg = pageOf(page)
    const alt = propFor(bildKey, pg)?.['x-cms-ui']?.alt
    if (!pg || !alt) return null
    const next = new Map(altPruefungen.value)
    next.set(fieldRef(pg, alt), { gruppe: pg, alt, bildFeld: bildKey, bild, seit: new Date().toISOString() })
    altPruefungen.value = next
    return alt
  }

  /** „Passt so“: Die Beschreibung bleibt, für genau dieses Bild. */
  async function altPasstSo(altKey: string, page?: string | null): Promise<boolean> {
    const c = ctx.value
    const pg = pageOf(page)
    const e = altPruefung(altKey, pg)
    if (!c || !e) return false
    try {
      await api.altPruefenErledigt(c.projectId, { site: c.site, lang: c.lang, page: pg, alt: altKey, bild: e.bild })
    } catch (err) {
      toast.error(uebersetze('alt.passt_so_fehler'), fehlersatz(err))
      return false
    }
    const next = new Map(altPruefungen.value)
    next.delete(fieldRef(pg, altKey))
    altPruefungen.value = next
    return true
  }

  function setField(key: string, value: string, page?: string | null): void {
    const pg = pageOf(page)
    if (!pg) return
    // Nur lokal updaten (optimistic). Save passiert erst bei saveNow().
    pageValues.value = {
      ...pageValues.value,
      [pg]: { ...(pageValues.value[pg] ?? {}), [key]: value },
    }
    const ref_ = fieldRef(pg, key)
    if (fieldErrors.value.has(ref_)) {
      const next = new Map(fieldErrors.value)
      next.delete(ref_)
      fieldErrors.value = next
    }
  }

  /**
   * Explicit Save (Cmd+S, Save-Button) — mit Toast + Preview-Rebuild.
   * Einziger Save-Trigger im System.
   */
  async function saveNow(): Promise<void> {
    return doSave({ silent: false, rebuild: true })
  }

  /**
   * NUR die Felder, die sich seit dem Laden geändert haben.
   *
   * Vorher ging immer der komplette Wertesatz der Gruppe zum Server, so wie er
   * beim Laden geholt wurde. Damit überschrieb der zweite Speichernde die
   * Änderungen des ersten mit seinem alten Ladestand — beide sahen
   * „Gespeichert", die Arbeit des einen war weg. Das trifft zwei Personen an
   * derselben Seite genauso wie eine Person mit zwei offenen Fenstern oder
   * einem Tab von heute früh (Audit 2026-08-05).
   *
   * Wer nur die Fußzeile ändert, schickt jetzt nur die Fußzeile. Alles andere
   * bleibt auf dem Server so, wie es dort steht.
   */
  function geaenderteWerte(pg: string): Record<string, string> {
    const werte = pageValues.value[pg] ?? {}
    const basis = pageBaseline.value[pg] ?? {}
    const raus: Record<string, string> = {}
    for (const k of Object.keys(werte)) {
      if ((werte[k] ?? '') !== (basis[k] ?? '')) raus[k] = werte[k] ?? ''
    }
    return raus
  }

  /** Seiten mit Änderungen — die aktive zuerst, damit ihr Ergebnis zuerst greift. */
  function dirtyPages(): string[] {
    const pages = new Set<string>()
    for (const ref_ of dirtyRefs.value) {
      pages.add(ref_.slice(0, ref_.indexOf('::')))
    }
    const active = ctx.value?.page
    const rest = [...pages].filter((p) => p !== active)
    return active && pages.has(active) ? [active, ...rest] : rest
  }

  async function doSave(opts: { silent: boolean; rebuild: boolean }): Promise<void> {
    if (!ctx.value) return
    if (saving.value) {
      pendingResave = true
      return
    }
    if (!isDirty.value) return

    saving.value = true
    try {
      // Jede geänderte Gruppe ist eine eigene Datei → ein Save-Aufruf pro Gruppe.
      // Beim ersten Fehlschlag wird abgebrochen: bereits gespeicherte Gruppen
      // bleiben gespeichert, der Rest bleibt sichtbar ungespeichert.
      let savedFields = 0
      let savedAny = false
      /** Gesetzt, wenn geschrieben, aber nicht committet oder nicht gepusht. */
      let nichtGesichert = ''
      const nextErrors = new Map<string, string>()

      for (const pg of dirtyPages()) {
        let result: SaveResponse
        // Den gesendeten Stand festhalten. Wer waehrend der laufenden Anfrage
        // weitertippt (Handy-Netz, der Server macht derweil commit und push),
        // darf sein Getipptes nicht als „gespeichert" verlieren: Die Baseline
        // unten wird aus GENAU diesem Stand gesetzt, nicht aus dem aktuellen.
        // Vorher galt alles als gespeichert, was beim Eintreffen der Antwort im
        // Feld stand — in der Datei stand es nie (Audit 2026-08-05).
        const gesendet = geaenderteWerte(pg)
        if (Object.keys(gesendet).length === 0) continue
        try {
          result = await api.saveProject(ctx.value.projectId, {
            site: ctx.value.site,
            lang: ctx.value.lang,
            page: pg,
            values: gesendet,
          })
        } catch (e) {
          if (e instanceof ApiError && e.status === 403) {
            /* Kein Recht (mehr), meist entzogen, während die Seite offen
               war. Die Rechte neu holen: Dann verschwinden „Bearbeiten" und
               der Speichern-Knopf von selbst, und das Feld nimmt keine
               Eingabe mehr an, statt dass jeder weitere Versuch an derselben
               Stelle scheitert. Das Getippte bleibt stehen, „Zurücksetzen"
               verwirft es. */
            const rechte = useKundenRechte()
            rechte.zuruecksetzen()
            void rechte.laden(ctx.value.projectId)
            toast.error(
              uebersetze('meldung.nicht_gespeichert'),
              uebersetze('meldung.kein_bearbeiten_recht'),
            )
          } else if (e instanceof ApiError) {
            toast.error(uebersetze('errors.save_failed'), fehlersatz(e))
          } else {
            toast.error(uebersetze('errors.network'), fehlersatz(e))
          }
          fieldErrors.value = nextErrors
          return
        }

        if ('saved' in result && result.saved) {
          // Nur die gesendeten Felder gelten als gespeichert. Der Rest der
          // Basis bleibt stehen — es wurde ja auch nur das Gesendete
          // geschrieben.
          pageBaseline.value = {
            ...pageBaseline.value,
            [pg]: { ...(pageBaseline.value[pg] ?? {}), ...gesendet },
          }
          // Mitgespeicherte Beschreibungen sind geprüft. Der Server räumt
          // seine Liste im selben Zug auf (AltPruefung::nachSpeichern).
          if (Object.keys(gesendet).some((k) => altPruefungen.value.has(fieldRef(pg, k)))) {
            const next = new Map(altPruefungen.value)
            for (const k of Object.keys(gesendet)) next.delete(fieldRef(pg, k))
            altPruefungen.value = next
          }
          savedFields += result.field_count
          savedAny = true
          // Geschrieben ja, gesichert nein: Ohne Commit oder Push liegt die
          // Änderung nur als Datei auf dem Server und kann beim nächsten
          // Abgleich verschwinden. Vorher stand hier trotzdem „Gespeichert ✓".
          if (result.git && (!result.git.committed || !result.git.pushed)) {
            nichtGesichert = result.git.error?.trim() || (
              result.git.committed
                ? uebersetze('meldung.nicht_bei_github')
                : uebersetze('meldung.nicht_in_versionsgeschichte')
            )
          }
        } else if ('error' in result) {
          if (result.errors && result.errors.length > 0) {
            for (const e of result.errors) {
              if (e.field) nextErrors.set(fieldRef(pg, e.field), pruefsatz(e))
            }
            const erster = result.errors[0]
            toast.error(
              uebersetze('meldung.pruefung.titel'),
              erster ? pruefsatz(erster) : uebersetze('meldung.pruefung.mehrere'),
            )
          } else {
            toast.error(
              uebersetze('meldung.speichern_abgelehnt'),
              result.message ?? uebersetze('meldung.unbekannter_fehler'),
            )
          }
          fieldErrors.value = nextErrors
          return
        }
      }

      fieldErrors.value = nextErrors
      if (savedAny) {
        lastSavedAt.value = Date.now()
        // Toast nur bei explizitem Save (Cmd+S/Button) — Auto-Save bleibt
        // unsichtbar damit Tipp-Pausen nicht durch ein Toast unterbrochen werden.
        // Der "Gespeichert"-Indicator in EditorPageBar (lastSavedAt) reicht.
        if (nichtGesichert !== '') {
          // Immer melden, auch beim stillen Speichern: Das ist keine
          // Bestätigung, sondern eine Warnung.
          toast.error(uebersetze('meldung.gespeichert_ungesichert'), nichtGesichert)
        } else if (!opts.silent) {
          toast.success(uebersetze('seite.gespeichert'), uebersetze('feld.anzahl', { n: savedFields }, savedFields))
        }
        // Preview-Rebuild NUR bei bewussten Save-Aktionen (Cmd+S/Button/Blur).
        // Auto-Save (Tipp-Pause) skipt Rebuild — sonst lädt das iframe mitten
        // im Tippen neu und unterbricht den Workflow.
        //
        // OHNE Wartezeit und OHNE zweiten Bau: Das Speichern hat serverseitig
        // schon einen Bau angestossen. Frueher wartete das Panel erst
        // anderthalb Sekunden und stiess dann einen ZWEITEN an, der in dieselbe
        // Sperre lief, bis zu zwanzig Sekunden wartete und dann alles noch
        // einmal baute. Deshalb dauerte es so lange, bis sich in der Vorschau
        // etwas tat (Davids Meldung 2026-08-05). Jetzt wird auf den laufenden
        // Bau gewartet und nur dann selbst gebaut, wenn gar keiner laeuft.
        if (opts.rebuild) {
          void wartAufVorschau()
        }
      }
    } finally {
      saving.value = false
      if (pendingResave) {
        pendingResave = false
        // Re-Trigger nach In-Flight-Save (race-condition: User hat während
        // dem Save nochmal saveNow gerufen) — gleiche opts wie ursprünglich.
        void doSave(opts)
      }
    }
  }

  /**
   * Struktur ändern: Absatz einfügen, verschieben oder löschen.
   *
   * Anders als beim Tippen ändert sich hier der Aufbau der Seite. Danach sind
   * die Block-Nummern andere — deshalb wird die Vorschau neu gebaut und die
   * Feldliste frisch geladen, statt lokal etwas zu raten.
   */
  async function blockAktion(
    block: string,
    aktion: 'davor' | 'danach' | 'hoch' | 'runter' | 'loeschen',
    text = '',
  ): Promise<void> {
    const c = ctx.value
    if (!c || strukturLaeuft.value) return
    if (isDirty.value) {
      toast.error(
        uebersetze('meldung.erst_speichern'),
        uebersetze('meldung.ungespeicherte_auf_dieser_seite'),
      )
      return
    }
    strukturLaeuft.value = true
    try {
      await api.docsBlockAktion(c.projectId, {
        site: c.site,
        page: c.page,
        lang: c.lang,
        block,
        aktion,
        text,
      })
      focusField(null)
      await rebuildNow()
      await load(c)
      const namen: Record<string, string> = {
        davor: 'meldung.absatz.eingefuegt',
        danach: 'meldung.absatz.eingefuegt',
        hoch: 'meldung.absatz.hoch',
        runter: 'meldung.absatz.runter',
        loeschen: 'meldung.absatz.geloescht',
      }
      toast.success(uebersetze(namen[aktion] ?? 'meldung.absatz.geaendert'), uebersetze('meldung.vorschau_aktuell'))
    } catch (e) {
      // Was am Absatz nicht ging, weiss nur der Server (etwa: schon der erste).
      toast.error(uebersetze('meldung.ging_nicht'), fehlersatz(e))
    } finally {
      strukturLaeuft.value = false
    }
  }

  async function rebuildNow(): Promise<void> {
    if (!ctx.value) return
    if (rebuilding.value) {
      // Zweiter Anlauf waehrend eines laufenden: vormerken statt wegwerfen.
      // Vorher verschwand er stillschweigend, und die Vorschau blieb auf dem
      // Stand von vorhin (Audit 2026-08-05).
      rebuildNachholen = true
      return
    }
    rebuilding.value = true
    try {
      const r = await api.rebuildPreview(ctx.value.projectId, {
        site: ctx.value.site,
        lang: ctx.value.lang,
        page: ctx.value.page,
      })
      if (r.ok) {
        // skipped = ein anderer Build für dasselbe Projekt lief schon (Staging-
        // Build nach dem Speichern, oder die Kollegin speichert gleichzeitig).
        // Kein Grund für eine Meldung — aber auch kein Grund, es dabei zu
        // belassen: Der andere Bau kann VOR der eigenen Änderung gestartet sein.
        // Vorher blieb `lastRebuildAt` in dem Fall stehen, und da nur dieser
        // Wert das Neuladen auslöst, zeigte die Vorschau dauerhaft den alten
        // Stand (Audit 2026-08-05). Also gleich noch einmal versuchen.
        if (r.skipped) {
          if (uebersprungen < MAX_UEBERSPRUNGEN) {
            uebersprungen++
            rebuildNachholen = true
          } else {
            uebersprungen = 0
            toast.error(uebersetze('meldung.vorschau_nicht_aktuell'), uebersetze('meldung.anderer_bau'))
          }
        } else {
          uebersprungen = 0
          lastRebuildAt.value = Date.now()
        }
      } else {
        // Die betroffenen Felder rot markieren, damit die Ursache dort sichtbar
        // ist, wo sie behoben wird — nicht nur im Toast.
        const bad = r.failed_fields ?? []
        if (bad.length > 0) {
          const next = new Map(fieldErrors.value)
          for (const key of bad) {
            next.set(fieldRef(pageOf(null), key), uebersetze('meldung.text_bricht_vorschau'))
          }
          fieldErrors.value = next
        }
        toast.error(
          uebersetze('meldung.vorschau_bau_fehler'),
          r.reason !== undefined && r.reason !== ''
            ? r.reason
            : `exit ${r.exit_code}: ${r.output_tail.slice(-200)}`,
        )
      }
    } catch (e) {
      toast.error(uebersetze('meldung.vorschau_anstossen_fehler'), fehlersatz(e))
    } finally {
      rebuilding.value = false
      if (rebuildNachholen) {
        rebuildNachholen = false
        // Kurz warten: Bei „uebersprungen" laeuft der andere Bau noch, ein
        // sofortiger Anlauf liefe nur wieder in dieselbe Sperre.
        window.setTimeout(() => { void rebuildNow() }, 2000)
      }
    }
  }

  /**
   * Wartet auf den Bau, den das Speichern angestossen hat, und laedt danach die
   * Vorschau neu. Stoesst nur dann selbst einen an, wenn gar keiner laeuft.
   *
   * Der Ablauf war vorher: anderthalb Sekunden warten, dann einen eigenen Bau
   * anfordern, der in die Sperre des laufenden lief, dort bis zu zwanzig
   * Sekunden wartete und anschliessend alles ein zweites Mal baute. Die Kundin
   * sah in der Zwischenzeit nichts.
   */
  async function wartAufVorschau(seit?: number): Promise<void> {
    const c = ctx.value
    if (!c) return
    const projektId = c.projectId
    // `seit`: Sekunden, als die Änderung abging. Der Server baut seit dem
    // 27.10.2026 gleich nach dem Commit; ein Bau, der vor dem Ende der Anfrage
    // fertig war, zeigt die Änderung also schon und muss nicht wiederholt werden.
    const start = seit ?? Math.floor(Date.now() / 1000)
    const ende = Date.now() + 120_000

    // Sofort anzeigen, dass gebaut wird — sonst sieht die Kundin nach dem
    // Speichern sekundenlang gar nichts und klickt noch einmal.
    rebuilding.value = true
    let selbstBauen = false
    try {
      // Kurz Zeit geben, den Hintergrund-Bau zu starten — er wird beim
      // Speichern abgesetzt und braucht einen Augenblick bis zur Sperre.
      await new Promise((r) => window.setTimeout(r, 250))

      while (Date.now() < ende) {
        let stand: PreviewStatusResponse
        try {
          stand = await api.previewStatus(projektId)
        } catch {
          selbstBauen = true
          break
        }
        if (stand.built_at !== null && stand.built_at >= start && !stand.building) {
          // Fertig und neuer als der Klick auf Speichern → neu laden.
          lastRebuildAt.value = Date.now()
          return
        }
        if (!stand.building) {
          // Es läuft keiner (mehr) und die Vorschau ist nicht neuer geworden:
          // entweder kam nie einer, oder er ist gescheitert. Selbst bauen.
          selbstBauen = true
          break
        }
        await new Promise((r) => window.setTimeout(r, 500))
      }
      if (!selbstBauen) {
        toast.error(uebersetze('meldung.vorschau_dauert'), uebersetze('meldung.vorschau_dauert_text'))
      }
    } finally {
      rebuilding.value = false
    }
    if (selbstBauen) await rebuildNow()
  }

  function focusField(
    key: string | null,
    page?: string | null,
    hint?: string | null,
    felder?: { id: string; art: string }[] | null,
  ): void {
    focusedField.value = key
    focusedPage.value = key === null ? null : (page ?? null)
    focusHint.value = key === null ? null : (hint ?? null)
    focusFelder.value = key === null ? [] : (felder ?? [])
    // Derselbe Klick auf dieselbe Stelle soll den Cursor wieder dorthin setzen.
    focusZaehler.value++
  }

  function resetField(key: string, page?: string | null): void {
    const original = pageBaseline.value[pageOf(page)]?.[key] ?? ''
    setField(key, original, page)
  }

  /**
   * Übernimmt das Feld-Inventar der Vorschau. `page: null` heißt „gehört zur
   * gezeigten Seite" — welche das ist, weiß nur das Panel, deshalb wird sie
   * hier eingesetzt.
   */
  function setInventory(fields: { id: string; page: string | null }[] | null, shownPage: string): void {
    if (fields === null) {
      inventory.value = null
      return
    }
    const seen = new Set<string>()
    const out: FieldRef[] = []
    for (const f of fields) {
      if (!f || typeof f.id !== 'string' || f.id === '') continue
      const pg = typeof f.page === 'string' && f.page !== '' ? f.page : shownPage
      const ref_ = fieldRef(pg, f.id)
      if (seen.has(ref_)) continue
      seen.add(ref_)
      out.push({ key: f.id, page: pg })
    }
    inventory.value = out
  }

  function $reset(): void {
    ctx.value = null
    schema.value = null
    pageValues.value = {}
    pageBaseline.value = {}
    referenceValues.value = {}
    focusedField.value = null
    focusedPage.value = null
    focusHint.value = null
    focusFelder.value = []
    inventory.value = null
    fieldErrors.value = new Map()
    fieldLabels.value = {}
    altPruefungen.value = new Map()
    pendingResave = false
  }

  return {
    ctx,
    schema,
    propFor,
    baseline,
    values,
    pageValues,
    pageBaseline,
    referenceValues,
    focusedField,
    focusedPage,
    focusHint,
    focusFelder,
    focusZaehler,
    inventory,
    loading,
    saving,
    rebuilding,
    lastSavedAt,
    lastRebuildAt,
    fieldErrors,
    dirty,
    offeneEntwuerfe,
    dirtyRefs,
    isDirty,
    ungespeicherteSeiten,
    allKeys,
    load,
    wechsleSeite,
    aktualisiereWerte,
    nachFremderAenderung,
    setField,
    setInventory,
    saveNow,
    rebuildNow,
    wartAufVorschau,
    blockAktion,
    strukturLaeuft,
    focusField,
    resetField,
    valueOf,
    isFieldDirty,
    fieldError,
    fieldLabel,
    hasPage,
    altPruefungen,
    altPruefung,
    bildGetauscht,
    altPasstSo,
    $reset,
  }
})
